import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/fileUrl";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReceiptText, FileText, FileSpreadsheet, Loader2, Users } from "lucide-react";
import { exportStatementPDF, exportStatementExcel, exportTeamReportPDF, exportTeamReportExcel } from "@/lib/commissionStatement";

export const CommissionStatement = () => {
  const { user } = useAuth();
  const isManager = ["admin", "manager"].includes(user?.role);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState("");

  const brandFrom = (org) => ({ name: org?.name, color: org?.brand_color, address: org?.address, logoUrl: org?.logo ? fileUrl(org.logo) : null });

  const run = async (kind) => {
    setBusy(kind);
    try {
      const [{ data }, orgRes] = await Promise.all([
        api.get(`/commission-statement?month=${month}`),
        api.get("/org").catch(() => ({ data: {} })),
      ]);
      if (!data.count) { toast.info("No won deals for that month yet"); return; }
      if (kind === "pdf") await exportStatementPDF(data, brandFrom(orgRes.data));
      else exportStatementExcel(data, brandFrom(orgRes.data));
      toast.success(`Statement ready — ${data.count} deal${data.count !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error("Could not build your commission statement");
    } finally { setBusy(""); }
  };

  const runTeam = async (kind) => {
    setBusy(kind);
    try {
      const [{ data }, orgRes] = await Promise.all([
        api.get(`/team-commission-report?month=${month}`),
        api.get("/org").catch(() => ({ data: {} })),
      ]);
      if (!data.agents) { toast.info("No won deals across the team that month yet"); return; }
      if (kind === "team-pdf") await exportTeamReportPDF(data, brandFrom(orgRes.data));
      else exportTeamReportExcel(data, brandFrom(orgRes.data));
      toast.success(`Team report ready — ${data.agents} agent${data.agents !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error("Could not build the team commission report");
    } finally { setBusy(""); }
  };

  return (
    <Card className="flex flex-col gap-3 p-5" data-testid="commission-statement-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground"><ReceiptText className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-semibold">Monthly commission statement</p>
            <p className="text-xs text-muted-foreground">Download your won deals and earned commission for a month.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[160px]" data-testid="statement-month-input" />
          <Button variant="outline" size="sm" onClick={() => run("pdf")} disabled={!!busy} data-testid="statement-pdf-button">
            {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => run("excel")} disabled={!!busy} data-testid="statement-excel-button">
            {busy === "excel" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Excel
          </Button>
        </div>
      </div>
      {isManager && (
        <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Users className="h-4 w-4" /> <span className="font-medium text-foreground">Team report</span> — every agent's earnings for the selected month
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => runTeam("team-pdf")} disabled={!!busy} data-testid="team-report-pdf-button">
              {busy === "team-pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Team PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => runTeam("team-excel")} disabled={!!busy} data-testid="team-report-excel-button">
              {busy === "team-excel" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Team Excel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
